import { useState } from 'react'
import { addDays, dayNumber, monthTitle, mondayOf, rangeLabel, weekdayShort } from '../lib/moscow-date'
import styles from './day-timeline.module.css'

export type DayState = 'published' | 'draft' | 'empty'

export type DayFace = { readonly src: string | null; readonly name: string }

export type DayInfo = { readonly state: DayState; readonly faces: readonly DayFace[]; readonly caption: string }

const STATE_LABELS: Readonly<Record<DayState, string>> = {
  published: 'готово',
  draft: 'черновик',
  empty: 'пусто',
}

const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс']

type Props = {
  today: string
  selected: string
  maxAhead: number
  label: string
  dayOf: (date: string) => DayInfo
  onSelect: (date: string) => void
  emptyAhead: number
  filling: boolean
  onFill: () => void
}

function monthStart(iso: string): string {
  return `${iso.slice(0, 8)}01`
}

function shiftMonth(iso: string, delta: number): string {
  const d = new Date(`${monthStart(iso)}T00:00:00.000Z`)
  d.setUTCMonth(d.getUTCMonth() + delta)
  return d.toISOString().slice(0, 10)
}

function Faces({ faces }: { faces: readonly DayFace[] }) {
  const trio = faces.length > 1
  return (
    <span className={styles.faces} data-trio={trio || undefined} aria-hidden="true">
      {faces.map((f, i) => (
        <span key={i} className={styles.face}>
          {f.src ? <img src={f.src} alt="" loading="lazy" draggable={false} /> : <span className={styles.faceName}>{f.name}</span>}
        </span>
      ))}
    </span>
  )
}

export function DayTimeline({ today, selected, maxAhead, label, dayOf, onSelect, emptyAhead, filling, onFill }: Props) {
  const [week, setWeek] = useState(() => mondayOf(selected))
  const [calendar, setCalendar] = useState<string | null>(null)
  const last = addDays(today, maxAhead)
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i))
  const select = (date: string) => {
    setWeek(mondayOf(date))
    setCalendar(null)
    onSelect(date)
  }
  const gridStart = calendar ? mondayOf(monthStart(calendar)) : null
  const month = gridStart ? Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)) : []

  return (
    <div className={styles.root}>
      <div className={styles.bar}>
        <button type="button" className={`secondary ${styles.round}`} aria-label="Предыдущая неделя" onClick={() => setWeek(addDays(week, -7))}>
          ‹
        </button>
        <span className={styles.range}>{rangeLabel(week, addDays(week, 6))}</span>
        <button type="button" className={`secondary ${styles.round}`} aria-label="Следующая неделя" onClick={() => setWeek(addDays(week, 7))}>
          ›
        </button>
        <button type="button" className="secondary" onClick={() => select(today)}>
          Сегодня
        </button>
        <button type="button" className="secondary" aria-expanded={calendar !== null} onClick={() => setCalendar(calendar ? null : selected)}>
          Календарь · {monthTitle(calendar ?? selected)}
        </button>
        <span className={styles.spacer} />
        <button type="button" disabled={filling || emptyAhead === 0} title="Пустые дни до 7 дней вперёд получат случайные карты с текстами по умолчанию, как черновики" onClick={onFill}>
          {filling ? 'Раскладываю…' : emptyAhead > 0 ? `Разложить пустые дни · ${emptyAhead}` : 'Пустых дней нет'}
        </button>
      </div>

      {calendar && (
        <div className={styles.calendar} role="dialog" aria-label="Выбор даты">
          <div className={styles.calHead}>
            <button type="button" className={`secondary ${styles.round}`} aria-label="Предыдущий месяц" onClick={() => setCalendar(shiftMonth(calendar, -1))}>
              ‹
            </button>
            <b>{monthTitle(calendar)}</b>
            <button type="button" className={`secondary ${styles.round}`} aria-label="Следующий месяц" onClick={() => setCalendar(shiftMonth(calendar, 1))}>
              ›
            </button>
          </div>
          <div className={styles.calGrid}>
            {WEEKDAYS.map((w) => (
              <span key={w} className={styles.calWeek}>
                {w}
              </span>
            ))}
            {month.map((d) => {
              const info = dayOf(d)
              return (
                <button
                  key={d}
                  type="button"
                  className={styles.calDay}
                  data-state={info.state}
                  data-out={d.slice(0, 7) !== calendar.slice(0, 7) || undefined}
                  data-today={d === today || undefined}
                  aria-pressed={d === selected}
                  disabled={d > last}
                  onClick={() => select(d)}
                >
                  {dayNumber(d)}
                  <span className={styles.calDot} aria-hidden="true" />
                </button>
              )
            })}
          </div>
          <span className={styles.note}>Готовить можно на {maxAhead} дней вперёд. Прошлые дни открываются только для просмотра.</span>
        </div>
      )}

      <div className={styles.week} role="group" aria-label={label}>
        {days.map((d) => {
          const info = dayOf(d)
          const locked = d > last
          return (
            <button
              key={d}
              type="button"
              className={styles.day}
              data-state={info.state}
              data-today={d === today || undefined}
              aria-pressed={d === selected}
              disabled={locked}
              onClick={() => select(d)}
            >
              <span className={styles.dayWeek}>{d === today ? 'сегодня' : weekdayShort(d)}</span>
              <span className={styles.dayNum}>{dayNumber(d)}</span>
              <span className={styles.dayState}>
                <span className={styles.dot} aria-hidden="true" />
                {locked ? 'позже' : STATE_LABELS[info.state]}
              </span>
              <Faces faces={info.faces} />
              <span className={styles.caption}>{info.caption}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
